/**
 * A renovação e estratégia da frota (módulo 20; 0107): o que a mesa e o
 * celular mostram e escrevem, sem rede, para o teste alcançar.
 *
 * Nenhuma conta mora aqui: o histórico econômico, a tendência, as
 * alternativas, a marca de candidata e o corte do caixa vêm do servidor, com
 * as premissas e as frases (o valor contábil que nunca se soma ao de mercado,
 * a candidata que é marca e não recomendação, a locação que é só premissa).
 * Aqui está o que é da tela — o endereço, o nome de cada coisa, o formato e o
 * retorno das gravações (R04).
 */
import { formatarDecimal, formatarInteiro, formatarReais, formatarCompetencia, formatarDia } from '@/lib/formato';
import type { RetornoDeclarado } from '@/lib/retorno';
import { termoDaBusca } from '@/lib/texto';

import type {
  Alternativa,
  FiltroDaFrota,
  GrupoDaPremissa,
  OrdemDaFrota,
  OrigemDaPremissa,
  PremissaDoEstudo,
  RespostaDaPremissa,
  Risco,
  SituacaoDaProposta,
  SituacaoDoEstudo,
  Tendencia,
  TipoDeProposta,
} from './api';

/* ----------------------------------------------------- o endereço das telas */

const ORDENS: readonly OrdemDaFrota[] = ['custo', 'tendencia', 'margem', 'disponibilidade', 'idade', 'vida', 'codigo'];
const FILTROS: readonly FiltroDaFrota[] = ['candidatas', 'tendencia_alta', 'horas_insuficientes', 'em_estudo', 'sem_estudo'];
export const OPCOES_POR_PAGINA_DA_FROTA = [25, 50, 100] as const;
export const POR_PAGINA_DA_FROTA = 25;

export interface BuscaDaRenovacao {
  readonly pagina?: number;
  readonly porPagina?: number;
  /** Ausente = `custo` (o custo total por hora, do maior para o menor). */
  readonly ordem?: Exclude<OrdemDaFrota, 'custo'>;
  readonly busca?: string;
  readonly tipo?: string;
  readonly filtro?: FiltroDaFrota;
}

/** O endereço da lista da frota: o que não serve é descartado, nunca corrigido no chute (p.16). */
export function validarBuscaDaRenovacao(entrada: Record<string, unknown>): BuscaDaRenovacao {
  const pagina = Number(entrada['pagina']);
  const porPagina = Number(entrada['porPagina']);
  const ordem = entrada['ordem'];
  /* Como foi digitado: o campo volta da URL a cada tecla, e aparar comia o espaco (`termoDaBusca`). */
  const busca = termoDaBusca(entrada['busca']) ?? '';
  const tipo = typeof entrada['tipo'] === 'string' ? entrada['tipo'].trim() : '';
  const filtro = entrada['filtro'];
  return {
    ...(Number.isInteger(pagina) && pagina > 1 ? { pagina } : {}),
    ...((OPCOES_POR_PAGINA_DA_FROTA as readonly number[]).includes(porPagina) && porPagina !== POR_PAGINA_DA_FROTA ? { porPagina } : {}),
    ...(typeof ordem === 'string' && ordem !== 'custo' && (ORDENS as readonly string[]).includes(ordem) ? { ordem: ordem as Exclude<OrdemDaFrota, 'custo'> } : {}),
    ...(busca ? { busca: busca.slice(0, 60) } : {}),
    ...(tipo ? { tipo: tipo.slice(0, 120) } : {}),
    ...(typeof filtro === 'string' && (FILTROS as readonly string[]).includes(filtro) ? { filtro: filtro as FiltroDaFrota } : {}),
  };
}

export const ABAS_DOS_INVESTIMENTOS = ['plano', 'propostas', 'criterios'] as const;
export type AbaDosInvestimentos = (typeof ABAS_DOS_INVESTIMENTOS)[number];

export interface BuscaDosInvestimentos {
  /** Ausente = `plano`. */
  readonly aba?: Exclude<AbaDosInvestimentos, 'plano'>;
}

export function validarBuscaDosInvestimentos(entrada: Record<string, unknown>): BuscaDosInvestimentos {
  const aba = entrada['aba'];
  return aba === 'propostas' || aba === 'criterios' ? { aba } : {};
}

/* ----------------------------------------------------- os nomes */

export const NOME_DA_ORDEM: Readonly<Record<OrdemDaFrota, string>> = {
  custo: 'Maior custo por hora',
  tendencia: 'Tendência de alta',
  margem: 'Menor margem por hora',
  disponibilidade: 'Menor disponibilidade',
  idade: 'Mais velhas',
  vida: 'Mais vida consumida',
  codigo: 'Código',
};

export const NOME_DO_FILTRO: Readonly<Record<FiltroDaFrota, string>> = {
  candidatas: 'Candidatas a renovação',
  tendencia_alta: 'Custo de manutenção subindo',
  horas_insuficientes: 'Horas insuficientes',
  em_estudo: 'Em estudo',
  sem_estudo: 'Sem estudo',
};

export const NOME_DA_SITUACAO_DO_ESTUDO: Readonly<Record<SituacaoDoEstudo, string>> = {
  rascunho: 'Em estudo',
  concluido: 'Concluído',
  arquivado: 'Arquivado',
};

/** O tom do selo do estudo: tons de retorno, nunca as cores dos seis status da máquina. */
export const TOM_DA_SITUACAO_DO_ESTUDO: Readonly<Record<SituacaoDoEstudo, 'sucesso' | 'atencao' | 'neutro'>> = {
  rascunho: 'atencao',
  concluido: 'sucesso',
  arquivado: 'neutro',
};

export const NOME_DA_SITUACAO_DA_PROPOSTA: Readonly<Record<SituacaoDaProposta, string>> = {
  proposta: 'Proposta',
  priorizada: 'Priorizada',
  aprovada: 'Aprovada',
  recusada: 'Recusada',
  adiada: 'Adiada',
};

export const TOM_DA_SITUACAO_DA_PROPOSTA: Readonly<Record<SituacaoDaProposta, 'sucesso' | 'atencao' | 'neutro' | 'erro'>> = {
  proposta: 'atencao',
  priorizada: 'atencao',
  aprovada: 'sucesso',
  recusada: 'erro',
  adiada: 'neutro',
};

export const NOME_DO_TIPO_DE_PROPOSTA: Readonly<Record<TipoDeProposta, string>> = {
  substituir_comprando: 'Substituir comprando',
  substituir_alugando: 'Substituir alugando',
  reformar: 'Reformar',
  comprar_adicional: 'Comprar adicional',
  alugar_adicional: 'Alugar adicional',
  vender_sem_repor: 'Vender sem repor',
};

/** A proposta que nasce da alternativa escolhida no estudo. */
export const TIPO_DA_ALTERNATIVA: Readonly<Partial<Record<Alternativa, TipoDeProposta>>> = {
  reformar: 'reformar',
  substituir_comprando: 'substituir_comprando',
  substituir_alugando: 'substituir_alugando',
  vender_sem_repor: 'vender_sem_repor',
};

export const NOME_DO_RISCO: Readonly<Record<Risco, string>> = { baixo: 'Baixo', medio: 'Médio', alto: 'Alto' };

export const NOME_DA_ORIGEM: Readonly<Record<OrigemDaPremissa, string>> = {
  sistema: 'Do sistema',
  informada: 'Informada',
  orcamento: 'Do orçamento',
};

export const GRUPOS_DE_PREMISSA: readonly (readonly [GrupoDaPremissa, string])[] = [
  ['demanda', 'Demanda, diesel e capital'],
  ['maquina_atual', 'A máquina hoje'],
  ['reforma', 'Reformar'],
  ['compra', 'Substituir comprando'],
  ['locacao', 'Substituir alugando'],
];

/* ----------------------------------------------------- os formatos */

/** "R$ 320,50/h"; o travessão sem base — nunca "R$ 0,00/h" de quem não tem hora. */
export function reaisPorHora(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : `${formatarReais(v)}/h`;
}

/** "95%" (de 0,95); o travessão sem base. */
export function percentual(v: number | null | undefined, casas: 0 | 1 = 0): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return `${casas === 0 ? formatarInteiro(Math.round(v * 100)) : formatarDecimal(v * 100, 1)}%`;
}

/** "4.832 h". */
export function horas(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : `${formatarInteiro(Math.round(v))} h`;
}

/** "EST-0007" e "PI-0012": o código do estudo e da proposta, na tela e no toast. */
export const codigoDoEstudo = (n: number) => `EST-${String(n).padStart(4, '0')}`;
export const codigoDaProposta = (n: number) => `PI-${String(n).padStart(4, '0')}`;

/** A tendência em palavras: "+R$ 1,50/h por mês", ou a abstenção do servidor como veio. */
export function textoDaTendencia(t: Pick<Tendencia, 'valor' | 'sentido' | 'abstencao'>): string {
  if (t.valor === null) return t.abstencao ?? 'sem tendência';
  if (t.sentido === 'estavel') return `estável (${t.valor < 0 ? '−' : '+'}${formatarReais(Math.abs(t.valor))}/h por mês)`;
  return `${t.valor < 0 ? '−' : '+'}${formatarReais(Math.abs(t.valor))}/h por mês`;
}

/** O valor da premissa com a unidade: "R$ 380.000,00", "20 dias", "Sim", "6,29 R$/L" → "R$ 6,29/L". */
export function premissaEscrita(p: Pick<PremissaDoEstudo, 'unidade' | 'valor' | 'simOuNao'>): string {
  if (p.valor === null) return 'Sem premissa';
  if (p.simOuNao) return p.valor === 1 ? 'Sim' : 'Não';
  switch (p.unidade) {
    case 'R$': return formatarReais(p.valor);
    case 'R$/h': return `${formatarReais(p.valor)}/h`;
    case 'R$/L': return `${formatarReais(p.valor)}/L`;
    case 'R$/mês': return `${formatarReais(p.valor)}/mês`;
    case '% a.a.': return `${formatarDecimal(p.valor, 2).replace(/,00$/, '')}% a.a.`;
    case 'h/mês': return `${formatarDecimal(p.valor, 1).replace(/,0$/, '')} h/mês`;
    default: return `${formatarDecimal(p.valor, 1).replace(/,0$/, '')} ${p.unidade}`;
  }
}

/** A idade: "8 anos" (pela fabricação) ou "8 anos na frota" (pela aquisição); o travessão sem ano. */
export function idadeEscrita(i: { readonly anos: number | null; readonly pelo: 'fabricacao' | 'aquisicao' | null }): string {
  if (i.anos === null) return '—';
  const n = `${String(i.anos)} ${i.anos === 1 ? 'ano' : 'anos'}`;
  return i.pelo === 'aquisicao' ? `${n} na frota` : n;
}

/** O período da janela: "10/2025 a 09/2026". */
export function janelaEscrita(de: string | null, ate: string | null): string {
  if (!de || !ate) return 'sem meses fechados';
  return `${formatarCompetencia(de)} a ${formatarCompetencia(ate)}`;
}

/* ----------------------------------------------------- os retornos (R04) */

/** O título do retorno da premissa. */
export const tituloDaPremissa = (r: Pick<RespostaDaPremissa, 'depois'>) => (r.depois === null ? 'Premissa removida' : 'Premissa gravada');

export function detalheDaPremissa(r: RespostaDaPremissa): Omit<RetornoDeclarado, 'titulo'> {
  const antes = r.antes === null ? 'sem valor' : premissaEscrita({ unidade: r.unidade, valor: r.antes, simOuNao: r.unidade === 'sim/não' });
  const depois = r.depois === null ? 'sem valor' : premissaEscrita({ unidade: r.unidade, valor: r.depois, simOuNao: r.unidade === 'sim/não' });
  return {
    alvo: { codigo: codigoDoEstudo(r.numero), nome: r.codigo },
    fatos: [r.premissa, `${antes} → ${depois}`, r.fonte],
    efeito: r.alternativasCalculadas === r.alternativasCalculadasAntes
      ? `${String(r.alternativasCalculadas)} de 6 alternativas calculadas`
      : { rotulo: 'Alternativas calculadas', antes: `${String(r.alternativasCalculadasAntes)} de 6`, depois: `${String(r.alternativasCalculadas)} de 6` },
    ver: `/equipamentos/renovacao/estudos/${r.estudoId}`,
  };
}

export function detalheDoEstudoAberto(r: { id: string; numero: number; codigo: string; horizonteMeses: number; preenchidas: number; avisos: readonly string[] }): Omit<RetornoDeclarado, 'titulo'> {
  return {
    ...(r.avisos.length > 0 ? { tom: 'ressalva' as const } : {}),
    alvo: { codigo: codigoDoEstudo(r.numero), nome: r.codigo },
    fatos: [`Horizonte de ${String(r.horizonteMeses)} meses`, `${String(r.preenchidas)} ${r.preenchidas === 1 ? 'premissa preenchida' : 'premissas preenchidas'} pelo sistema e pelo orçamento`],
    efeito: r.avisos[0] ?? 'O valor de mercado é informado por você, com a fonte',
    ver: `/equipamentos/renovacao/estudos/${r.id}`,
  };
}

export function detalheDaConclusao(r: { id: string; numero: number; codigo: string; alternativa: string; custoPorHora: number | null; total: number | null; concluidoPor: string | null }): Omit<RetornoDeclarado, 'titulo'> {
  return {
    alvo: { codigo: codigoDoEstudo(r.numero), nome: r.codigo },
    fatos: [`Recomendação: ${r.alternativa}`, r.custoPorHora === null ? null : `${reaisPorHora(r.custoPorHora)} no horizonte`, r.total === null ? null : `${formatarReais(r.total)} no total`],
    efeito: 'Os números ficaram congelados e não mudam',
    ver: `/equipamentos/renovacao/estudos/${r.id}`,
  };
}

/** "Proposta aprovada", "Proposta adiada". */
export const tituloDaDecisao = (depois: SituacaoDaProposta) => `Proposta ${NOME_DA_SITUACAO_DA_PROPOSTA[depois].toLowerCase()}`;

export function detalheDaDecisao(r: { numero: number; descricao: string; antes: SituacaoDaProposta; depois: SituacaoDaProposta; investimento: number | null; decididaPor: string | null }): Omit<RetornoDeclarado, 'titulo'> {
  return {
    ...(r.depois === 'recusada' || r.depois === 'adiada' ? { tom: 'ressalva' as const } : {}),
    alvo: { codigo: codigoDaProposta(r.numero), nome: r.descricao },
    fatos: [r.investimento === null ? null : formatarReais(r.investimento), r.decididaPor ? `Decidida por ${r.decididaPor}` : null],
    efeito: { rotulo: 'Situação', antes: NOME_DA_SITUACAO_DA_PROPOSTA[r.antes], depois: NOME_DA_SITUACAO_DA_PROPOSTA[r.depois] },
    ver: '/investimentos?aba=propostas',
  };
}

export function detalheDoOrcamento(r: { proposta: number; exercicio: number; versao: number; revisaoAberta: number | null; centro: string | null; competencia: string; valor: number | null }): Omit<RetornoDeclarado, 'titulo'> {
  return {
    alvo: { codigo: codigoDaProposta(r.proposta), nome: `Orçamento ${String(r.exercicio)}, versão ${String(r.versao)} em rascunho` },
    fatos: [r.valor === null ? null : formatarReais(r.valor), `Em ${formatarCompetencia(r.competencia)}`, r.centro ? `Centro ${r.centro}` : null],
    efeito: r.revisaoAberta === null ? 'A aprovação da versão é de quem aprova o orçamento' : `Revisão ${String(r.revisaoAberta)} aberta: a aprovação dela é de quem aprova o orçamento`,
    ver: '/orcamento',
  };
}

/** "02/10/2026", ou o travessão. */
export const diaOuTravessao = (d: string | null | undefined) => (d ? formatarDia(d.slice(0, 10)) : '—');

/**
 * O ESTUDO QUE A PROPOSTA USA (31/10/2026): escolhida a máquina, a janela já
 * vem com o estudo concluído mais recente dela — é ele que fundamenta a
 * proposta, e quem propõe pode trocar ou deixar sem estudo.
 */
export function estudoMaisRecente(
  estudos: readonly { readonly id: string; readonly numero: number; readonly equipamentoId: string }[],
  equipamentoId: string,
): string {
  let maior: { readonly id: string; readonly numero: number } | null = null;
  for (const e of estudos) {
    if (e.equipamentoId === equipamentoId && (maior === null || e.numero > maior.numero)) maior = e;
  }
  return maior?.id ?? '';
}
